import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from '../apiClient';
import { PaginationQueryParams } from 'types/queryTypes';
import { SortOrder } from 'hooks/useTableControls';
import {
  CreateCanvasProcessMapPayload,
  CreateProcessMapPayload,
  ProcessMapDto,
  ProcessMapTable,
} from 'types/processesTypes';
import {
  EditProcessBlockPayload,
  OutputCellResponse,
  ProcessBlockData,
  OutputCellConnectionsResponse,
} from 'types/processBlockTypes';

const PROCESS_MAP_QUERY_KEY = 'process-maps';
const PROCESS_BY_ID_QUERY = 'process-map-by-id';
const PROCESS_BLOCK_BY_ID = 'process-block-by-id';
const PROCESS_OUTPUT_CELL_CONNECTIONS = 'process-output-cell-connections';
const REACT_APP_DARWIN_URL = import.meta.env.VITE_DARWIN_URL;

/**
 * Get all process maps by company (active or archived)
 */
export const useGetProcessMapsByCompany = ({
  companyId,
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'name', order: 'ASC' },
  isArchived = false,
}: PaginationQueryParams & { companyId: string } & {
  sortOrder: SortOrder;
  isArchived?: boolean;
}) =>
  useQuery({
    queryKey: [
      PROCESS_MAP_QUERY_KEY,
      limit,
      page,
      query,
      sortOrder,
      isArchived,
    ],
    queryFn: () =>
      fetchFromApi<ProcessMapTable[]>(
        `${REACT_APP_DARWIN_URL}/traceability/${companyId}/plan?limit=${limit}&page=${page}&search=${query}&order=${sortOrder.order}&sortBy=${sortOrder.sortBy}&isArchived=${isArchived}`
      ),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });

/**
 * get process map by id
 */
export const useGetProcessMapById = (
  companyId: string,
  traceabilityId: string
) => {
  return useQuery({
    queryKey: [PROCESS_BY_ID_QUERY, companyId, traceabilityId],
    queryFn: () =>
      fetchFromApi<ProcessMapDto>(
        `${REACT_APP_DARWIN_URL}/traceability/${companyId}/plan/${traceabilityId}`
      ),
    enabled: !!companyId && !!traceabilityId,
    refetchOnWindowFocus: false,
  });
};

/**
 * Post create process map
 */
export const useCreateProcessMap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
    }: {
      payload: CreateProcessMapPayload;
      companyId: string | number;
    }) =>
      fetchFromApi(`${REACT_APP_DARWIN_URL}/traceability/${companyId}/plan`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_MAP_QUERY_KEY] });
    },
  });
};

/**
 * Update a process map
 */
export const useUpdateProcessMapByID = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      traceabilityId,
    }: {
      payload: CreateProcessMapPayload;
      companyId: string | number;
      traceabilityId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/traceability/${companyId}/plan/${traceabilityId}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_MAP_QUERY_KEY] });
    },
  });
};

/**
 * Change status to process map
 */
export const useChangeStatusProcessMapById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      traceabilityId,
    }: {
      payload: { status: string };
      companyId: string | number;
      traceabilityId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/traceability/${companyId}/plan/${traceabilityId}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_MAP_QUERY_KEY] });
    },
  });
};

/**
 * Change status to process map nodes
 */
export const useChangeStatusProcessNodesMapById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      traceabilityId,
    }: {
      payload: { status: string };
      companyId: string | number;
      traceabilityId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/traceability/${companyId}/plan/${traceabilityId}/nodes-status`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_MAP_QUERY_KEY] });
    },
  });
};

/**
 * Create canvas process map
 */
export const useCreateCanvasProcessMap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      planId,
    }: {
      payload: CreateCanvasProcessMapPayload;
      companyId: string | number;
      planId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/process-flow/${companyId}/plan/${planId}/map`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_MAP_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PROCESS_BY_ID_QUERY] });
    },
  });
};

/**
 * Get canvas process map by id
 */
export const useGetCanvasProcessMapById = (
  companyId: string,
  planId: string
) => {
  return useQuery({
    queryKey: [PROCESS_BY_ID_QUERY, companyId, planId],
    queryFn: () =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/process-flow/${companyId}/plan/${planId}/map`
      ),
    enabled: !!companyId && !!planId,
    refetchOnWindowFocus: true,
  });
};

/**
 * Update canvas status process block by ID
 */

export const useUpdateStatusCanvasProcessMapByID = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      planId,
      processFlowId,
      payload,
    }: {
      companyId: string;
      planId: string;
      processFlowId: string;
      payload: { status: string };
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/process-flow/${companyId}/plan/${planId}/node/${processFlowId}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_BY_ID_QUERY] });
    },
  });
};

/**
 * Get process block by Id
 */

export const useGetProcessBlockById = ({
  companyId,
  traceabilityPlanId,
  processFlowId,
}: {
  companyId: string;
  traceabilityPlanId: string;
  processFlowId: string;
}) => {
  return useQuery({
    queryKey: [
      PROCESS_BLOCK_BY_ID,
      companyId,
      traceabilityPlanId,
      processFlowId,
    ],
    queryFn: () =>
      fetchFromApi<ProcessBlockData>(
        `${REACT_APP_DARWIN_URL}/process-flow/${companyId}/plan/${traceabilityPlanId}/node/${processFlowId}`
      ),
    enabled: !!companyId && !!traceabilityPlanId && !!processFlowId,
    refetchOnWindowFocus: false,
  });
};

/**
 * Get process block by Id
 */

export const useUpdateProcessBlockId = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      traceabilityPlanId,
      processFlowId,
      payload,
    }: {
      payload: EditProcessBlockPayload;
      companyId: string;
      traceabilityPlanId: string;
      processFlowId: string;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/process-flow/${companyId}/plan/${traceabilityPlanId}/node/${processFlowId}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_BLOCK_BY_ID] });
      queryClient.invalidateQueries({ queryKey: [PROCESS_BY_ID_QUERY] });
    },
  });
};

/**
 * get output cell data by output cell id
 */
export const useGetOutputCellById = ({
  companyId,
  outputCellId,
  page = 1,
  limit = 10,
  sortOrder = { sortBy: 'transactionDate', order: 'ASC' },
  planId,
  connectionId,
}: PaginationQueryParams & {
  companyId: string;
  outputCellId: string | number;
  planId: string;
  connectionId?: string;
} & {
  sortOrder: SortOrder;
}) => {
  return useQuery({
    queryKey: [
      PROCESS_BY_ID_QUERY,
      companyId,
      planId,
      limit,
      page,
      sortOrder,
      outputCellId,
      connectionId,
    ],
    queryFn: () =>
      fetchFromApi<OutputCellResponse>(
        `${REACT_APP_DARWIN_URL}/process-flow/${companyId}/plan/${planId}/output-cell/${outputCellId}?limit=${limit}&page=${page}&order=${
          sortOrder.order
        }&sortBy=${sortOrder.sortBy}${
          connectionId ? `&connectionId=${connectionId}` : ''
        }`
      ),
    enabled: !!companyId && !!outputCellId,
    refetchOnWindowFocus: false,
    placeholderData: previousData => previousData,
  });
};

/**
 * Get output cell connections metadata
 */
export const useGetOutputCellConnections = ({
  companyId,
  outputCellId,
  planId,
}: {
  companyId: string;
  outputCellId: string | number;
  planId: string;
}) => {
  return useQuery({
    queryKey: [
      PROCESS_OUTPUT_CELL_CONNECTIONS,
      companyId,
      outputCellId,
      planId,
    ],
    queryFn: () =>
      fetchFromApi<OutputCellConnectionsResponse>(
        `${REACT_APP_DARWIN_URL}/process-flow/${companyId}/plan/${planId}/output-cell/${outputCellId}/connections`
      ),
    enabled: !!companyId && !!outputCellId && !!planId,
    refetchOnWindowFocus: false,
  });
};

/**
 *
 * Disconnect process block
 */
export const useDisconnectProcessBlock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      planId,
      outputCellId,
      connectionId,
    }: {
      companyId: string | number;
      planId: string | number;
      outputCellId: string | number;
      connectionId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/process-flow/${companyId}/plan/${planId}/output-cell/${outputCellId}/disconnect/${connectionId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_BY_ID_QUERY] });
    },
  });
};

/**
 * Duplicate process map
 */
export const useDuplicateProcessMap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      traceabilityPlanId,
      companyId,
    }: {
      traceabilityPlanId: string | number;
      companyId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/traceability/${companyId}/plan/${traceabilityPlanId}/duplicate`,
        {
          method: 'POST',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_MAP_QUERY_KEY] });
    },
  });
};

/**
 * Archive process map
 */
export const useArchiveProcessMap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      traceabilityId,
    }: {
      payload: { isArchived: boolean };
      companyId: string | number;
      traceabilityId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/traceability/${companyId}/plan/${traceabilityId}/archive`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROCESS_MAP_QUERY_KEY] });
    },
  });
};

/**
 * Delete Process Map EUDR doc by id
 */

export const useDeleteEUDRDocById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      planId,
      fileId,
    }: {
      companyId: string;
      planId: string;
      fileId: string;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/traceability/${companyId}/plan/${planId}/eudr-files/${fileId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PROCESS_BY_ID_QUERY],
      });
    },
  });
};
